import { Button, Dialog, Modal, ModalRef } from 'alurkerja-ui';
import { FormContainer } from '@/components/form/FormContainer';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  customFieldSchema,
  ICustomFieldSchema,
  CustomFieldService,
} from '@/api/customField';
import { UpdateProjectForm } from './FormUpdateProject';
import React, { useRef } from 'react';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { ISelectOption } from '@/components/form/fields/SelectField';
import { FaChartBar } from 'react-icons/fa';

type AddRemoveProjectModalProps = {
  id: string;
  customFieldData: ICustomFieldSchema;
  projectData: ISelectOption[]; // Adjust the type based on actual projectData structure
  onSuccess: () => void;
};

export const AddRemoveProjectModal: React.FC<AddRemoveProjectModalProps> = ({
  id,
  customFieldData,
  projectData,
  onSuccess,
}) => {
  const ref = useRef<ModalRef>(null);

  const { mutate: update, isPending: isUpdateLoading } =
    CustomFieldService.useUpdate();

  const form = useForm<ICustomFieldSchema>({
    resolver: zodResolver(customFieldSchema),
    defaultValues: customFieldData,
  });

  const projectIds =
    customFieldData?.project_custom_field?.map(item => item.project_id) || [];

  const onSubmit = (data: ICustomFieldSchema) => {
    const custom_option = data.options?.map(option => option.option) ?? [];

    const updatedProjectCustomField = data.project_ids.map(project_id => ({
      custom_field_id: data.id ?? '',
      project_id: project_id,
      id:
        customFieldData?.project_custom_field?.find(
          pcf => pcf.project_id === project_id,
        )?.id ?? '',
    }));

    const mergedData = {
      ...data,
      custom_option: custom_option,
      project_custom_field: updatedProjectCustomField,
    };

    if (id) {
      update(mergedData, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Custom field has been updated',
            callback: () => {
              onSuccess();
              ref.current?.closeModal();
            },
          });
        },
        onError: () => {
          Dialog.error({
            title: 'Error',
            description: 'Failed to Update Custom Field',
          });
        },
      });
    }
  };

  return (
    <Modal
      ref={ref}
      triggerButton={
        <Button
          className="bg-yellow-500"
        >
          <FaChartBar />
        </Button>
      }
      title="Edit Project for Custom Field"
    >
      {({ closeModal }) => (
        <div className="p-4">
          <FormContainer form={form} onSubmit={onSubmit}>
            <UpdateProjectForm
              projectIdsData={projectIds}
              projectData={projectData}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button loading={isUpdateLoading} type="submit">
                Save
              </Button>
              <OutlineButton onClick={closeModal}>Cancel</OutlineButton>
            </div>
          </FormContainer>
        </div>
      )}
    </Modal>
  );
};
